import { HttpError } from "ra-core";

/**
 * Minimal HTTP client for the zInspector public API.
 *
 * Builds call the API directly (the hosted domains are on the key's whitelist).
 * In dev, `localhost:<port>` cannot be whitelisted, so calls go to `/zi-api` and
 * Vite's dev proxy forwards them with `Origin: https://localhost.com`
 * (see vite.config.ts and zcrm-docs/SETUP.md, step 21).
 * MODE, not DEV: Cloudflare builds with NODE_ENV=CI, which makes DEV true in a build.
 */
export const ZINSPECTOR_API_URL =
  import.meta.env.VITE_ZINSPECTOR_API_URL ??
  (import.meta.env.MODE === "development"
    ? "/zi-api"
    : "https://portfolio.zinspector.com/api");

const SESSION_STORAGE_KEY = "zcrm.session";

export interface ZiUser {
  id: number;
  FirstName?: string;
  LastName?: string;
  email?: string;
  role?: string;
}

/** What sign-in stores: the encoded API key and the zInspector user picked as "me". */
export interface ZiSession {
  apiKey: string;
  user: ZiUser;
}

const storages = (): Storage[] => [sessionStorage, localStorage];

export const getSession = (): ZiSession | null => {
  for (const storage of storages()) {
    try {
      const raw = storage.getItem(SESSION_STORAGE_KEY);
      if (raw) return JSON.parse(raw) as ZiSession;
    } catch {
      // Unreadable storage (private mode) or corrupted value: treat as signed out.
    }
  }
  return null;
};

/** Kept in sessionStorage (cleared when the tab closes), or localStorage with "remember me". */
export const saveSession = (session: ZiSession, remember: boolean): void => {
  clearSession();
  (remember ? localStorage : sessionStorage).setItem(
    SESSION_STORAGE_KEY,
    JSON.stringify(session),
  );
};

export const clearSession = (): void => {
  for (const storage of storages()) {
    try {
      storage.removeItem(SESSION_STORAGE_KEY);
    } catch {
      // Ignore: nothing to clear.
    }
  }
};

export type QueryParams = Record<
  string,
  string | number | boolean | null | undefined
>;

const buildUrl = (path: string, params: QueryParams = {}): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") {
      search.set(key, String(value));
    }
  }
  const query = search.toString();
  return `${ZINSPECTOR_API_URL}${path}${query ? `?${query}` : ""}`;
};

const errorMessage = (status: number, body: unknown): string => {
  const detail =
    body && typeof body === "object" && "detail" in body
      ? String((body as { detail: unknown }).detail)
      : "";
  if (status === 403) {
    return detail
      ? `zInspector refused the request: ${detail}`
      : "zInspector refused the request (check the API key and its domain whitelist).";
  }
  if (status === 429)
    return "zInspector is throttling requests, try again in a minute.";
  return detail || `zInspector request failed (HTTP ${status}).`;
};

/** How requests reach zInspector: `fetch` against the real API, or the demo's in-browser fake. */
export type ZiTransport = (url: string, init: RequestInit) => Promise<Response>;

let transport: ZiTransport = (url, init) => fetch(url, init);
let usingFakeApi = false;

/**
 * Sends every request to `fake` instead of the network (demo build only). Everything above the
 * transport (mappers, data provider, screens) runs exactly as it does against the real API.
 */
export const connectFakeZinspector = (fake: ZiTransport): void => {
  transport = fake;
  usingFakeApi = true;
};

/** True in the demo, whose fake zInspector also accepts the writes zCRM proposes (new clients). */
export const isFakeZinspector = (): boolean => usingFakeApi;

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH";
  params?: QueryParams;
  body?: unknown;
  /** Overrides the stored session key (used to validate a key before saving it). */
  apiKey?: string;
}

export const ziFetch = async <T>(
  path: string,
  { method = "GET", params, body, apiKey }: RequestOptions = {},
): Promise<T> => {
  const key = apiKey ?? getSession()?.apiKey;
  if (!key) throw new HttpError("Not signed in", 401);

  const headers: Record<string, string> = {
    Accept: "application/json",
    "x-api-key": key,
  };
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const response = await transport(buildUrl(path, params), {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  const text = await response.text();
  let json: unknown = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = text;
  }

  if (!response.ok) {
    throw new HttpError(
      errorMessage(response.status, json),
      response.status,
      json,
    );
  }
  return json as T;
};

interface Page<T> {
  results: T[];
  next?: string | null;
}

const MAX_PAGES = 200;

/**
 * Reads every row of a list route. Handles both plain arrays and paginated
 * `{results, next}` responses (cursor or page based). `next` is an absolute URL on
 * the API host, so only its query string is reused: in dev the request must keep
 * going through the proxy.
 */
export const ziFetchAll = async <T>(
  path: string,
  params: QueryParams = {},
): Promise<T[]> => {
  const rows: T[] = [];
  let pageParams: QueryParams = { page_size: 500, ...params };
  for (let page = 0; page < MAX_PAGES; page++) {
    const response = await ziFetch<T[] | Page<T>>(path, { params: pageParams });
    if (Array.isArray(response)) return [...rows, ...response];
    rows.push(...(response.results ?? []));
    if (!response.next) return rows;
    pageParams = Object.fromEntries(
      new URL(response.next, "https://placeholder.invalid").searchParams,
    );
  }
  throw new Error(`zInspector list ${path} has more than ${MAX_PAGES} pages`);
};
